import { api, extractApiErrorMessage } from '../api/client';
import { ROUTES } from '../api/routes';

type UserSecurityRequestOptions = {
  bearerToken?: string;
};

export type AuthorizedDevice = {
  id: string;
  nome: string | null;
  navegador: string | null;
  sistemaOp: string | null;
  ip: string | null;
  aprovado: boolean;
  ultimoAcesso: string | null;
  esteDispositivo?: boolean;
  fingerprint: string;
};

export type SecurityHistoryEntry = {
  id: string;
  acao: string;
  ip: string | null;
  userAgent: string | null;
  criadoEm: string;
  restricao: string | null;
};

export type UserSecurityConfig = {
  trustLevel: number;
  temFcm: boolean;
  confirmacaoCelularNovoDispositivo: boolean;
  alertasAcessoSuspeito: boolean;
};

function authSurfaceConfig(options?: UserSecurityRequestOptions) {
  const config: {
    headers: { 'X-AC-Use-Bearer': 'true' };
    _acBearerToken?: string;
  } = {
    headers: {
      'X-AC-Use-Bearer': 'true',
    },
  };
  const bearerToken = options?.bearerToken?.trim();
  if (bearerToken) {
    config._acBearerToken = bearerToken;
  }
  return config;
}

export async function listAuthorizedDevices(
  options?: UserSecurityRequestOptions,
): Promise<AuthorizedDevice[]> {
  try {
    const { data } = await api.get<AuthorizedDevice[]>(
      ROUTES.security.dispositivos,
      authSurfaceConfig(options),
    );
    return Array.isArray(data) ? data : [];
  } catch (err) {
    throw new Error(extractApiErrorMessage(err, 'Falha ao carregar dispositivos.'));
  }
}

export async function listSecurityHistory(
  limite = 30,
  options?: UserSecurityRequestOptions,
): Promise<SecurityHistoryEntry[]> {
  try {
    const { data } = await api.get<SecurityHistoryEntry[] | { dados?: SecurityHistoryEntry[] }>(
      ROUTES.security.historico(limite),
      authSurfaceConfig(options),
    );
    return Array.isArray(data) ? data : data.dados ?? [];
  } catch (err) {
    throw new Error(extractApiErrorMessage(err, 'Falha ao carregar historico de seguranca.'));
  }
}

export async function getUserSecurityConfig(
  options?: UserSecurityRequestOptions,
): Promise<UserSecurityConfig> {
  try {
    const { data } = await api.get<UserSecurityConfig>(
      ROUTES.security.config,
      authSurfaceConfig(options),
    );
    return data;
  } catch (err) {
    throw new Error(extractApiErrorMessage(err, 'Falha ao carregar configuracao de seguranca.'));
  }
}

export async function revokeAuthorizedDevice(
  id: string,
  options?: UserSecurityRequestOptions,
): Promise<unknown> {
  try {
    const { data } = await api.delete(
      ROUTES.security.dispositivo(id),
      authSurfaceConfig(options),
    );
    return data;
  } catch (err) {
    throw new Error(extractApiErrorMessage(err, 'Falha ao revogar dispositivo.'));
  }
}

export async function updateUserSecurityConfig(
  patch: Partial<UserSecurityConfig>,
  options?: UserSecurityRequestOptions,
): Promise<unknown> {
  try {
    const { data } = await api.patch(
      ROUTES.security.config,
      patch,
      authSurfaceConfig(options),
    );
    return data;
  } catch (err) {
    throw new Error(extractApiErrorMessage(err, 'Falha ao atualizar configuracao de seguranca.'));
  }
}
